// Motion tiers (HANDOVER §5.3): one decision every animated component reads.
import { useEffect, useSyncExternalStore } from 'react'
import { create } from 'zustand'
import { useSettings, type MotionChoice } from '../data/settings'

export type MotionTier = 'full' | 'calm' | 'still'

export interface TierInputs {
  choice: MotionChoice
  reducedMotion: boolean
  saveData: boolean
  battery: { level: number; charging: boolean } | null
  /** Median frame time over the last sample window, ms. */
  medianFrameMs: number | null
}

export const SLOW_FRAME_MS = 22
export const LOW_BATTERY = 0.2
export const FLAT_MAP_BATTERY = 0.15

export function decideTier(i: TierInputs): MotionTier {
  if (i.choice === 'still' || i.reducedMotion) return 'still'
  if (i.choice === 'calm') return 'calm'
  if (i.saveData) return 'calm'
  if (i.battery && !i.battery.charging && i.battery.level < LOW_BATTERY) return 'calm'
  if (i.medianFrameMs !== null && i.medianFrameMs > SLOW_FRAME_MS) return 'calm'
  return 'full'
}

/** Below 15% (not charging) the 3D map rests and the flat map shows instead. */
export function preferFlatMap(i: Pick<TierInputs, 'battery'>): boolean {
  return !!i.battery && !i.battery.charging && i.battery.level < FLAT_MAP_BATTERY
}

interface Signals {
  battery: TierInputs['battery']
  medianFrameMs: number | null
}
export const useTierSignals = create<Signals>(() => ({ battery: null, medianFrameMs: null }))

const reducedQuery = typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : null
const subscribeReduced = (cb: () => void) => {
  reducedQuery?.addEventListener('change', cb)
  return () => reducedQuery?.removeEventListener('change', cb)
}

/** Dev/test builds only: screenshot scripts on slow software GL pin the Full tier. */
const FORCE_FULL = (() => {
  if (!(import.meta.env.DEV || import.meta.env.VITE_MAP_DEBUG)) return false
  try {
    return sessionStorage.getItem('tq-force-full') === '1'
  } catch {
    return false
  }
})()

export function useMotionTier(): MotionTier {
  const choice = useSettings((s) => s.motion)
  const reducedMotion = useSyncExternalStore(subscribeReduced, () => reducedQuery?.matches ?? false)
  const { battery, medianFrameMs } = useTierSignals()
  const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData ?? false
  if (FORCE_FULL) return 'full'
  return decideTier({ choice, reducedMotion, saveData, battery, medianFrameMs })
}

export function useBatteryPrefersFlat(): boolean {
  return preferFlatMap({ battery: useTierSignals((s) => s.battery) })
}

/** For animation loops (the diorama) that already measure their own frames. */
export function reportFrameTimes(medianMs: number) {
  const prev = useTierSignals.getState().medianFrameMs
  if (prev === null || prev <= SLOW_FRAME_MS) useTierSignals.setState({ medianFrameMs: medianMs })
}

interface BatteryLike extends EventTarget {
  level: number
  charging: boolean
}

/** Starts the battery listener and the frame-time monitor. Mount once at the app root. */
export function useTierMonitors() {
  useEffect(() => {
    let battery: BatteryLike | null = null
    const onBattery = () => battery && useTierSignals.setState({ battery: { level: battery.level, charging: battery.charging } })
    const nav = navigator as Navigator & { getBattery?: () => Promise<BatteryLike> }
    void nav.getBattery?.().then((b) => {
      battery = b
      onBattery()
      b.addEventListener('levelchange', onBattery)
      b.addEventListener('chargingchange', onBattery)
    })

    // Median frame time over three 2 s windows after the app becomes visible, then the loop stops
    // (an always-on rAF loop costs battery). Busy surfaces like the Map report via reportFrameTimes().
    // Once slow, it stays calm for the session.
    let raf = 0
    let windows = 0
    let last = 0
    let samples: number[] = []
    let windowStart = 0
    const tick = (t: number) => {
      if (last) samples.push(t - last)
      last = t
      if (!windowStart) windowStart = t
      if (t - windowStart >= 2000 && samples.length > 10) {
        const sorted = [...samples].sort((a, b) => a - b)
        const median = sorted[Math.floor(sorted.length / 2)] ?? 16
        const prev = useTierSignals.getState().medianFrameMs
        if (prev === null || prev <= SLOW_FRAME_MS) useTierSignals.setState({ medianFrameMs: median })
        samples = []
        windowStart = t
        if (++windows >= 3) return
      }
      raf = requestAnimationFrame(tick)
    }
    const onVis = () => {
      cancelAnimationFrame(raf)
      last = 0
      windowStart = 0
      windows = 0
      samples = []
      if (document.visibilityState === 'visible') raf = requestAnimationFrame(tick)
    }
    onVis()
    document.addEventListener('visibilitychange', onVis)
    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('visibilitychange', onVis)
      battery?.removeEventListener('levelchange', onBattery)
      battery?.removeEventListener('chargingchange', onBattery)
    }
  }, [])
}
